import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Stomp } from '@stomp/stompjs';
import { WS_URL } from '../common/Constants';
import Cookies from '../common/Cookies';
import { useAuth } from './AuthContext';
import { NOTIFICATION_TYPES } from '../utils/notificationTypes';

// Helper to get auth token (cookie or localStorage)
const getAuthToken = () => {
  return Cookies.getJwtToken() || localStorage.getItem('jwtToken') || null;
};

// Helper to get tenant ID only when we have an authenticated user
const getTenantId = (user) => {
  if (user) {
    return user.tenant_id || user.tenantId || null;
  }
  return null;
};

const UserNotificationContext = createContext();

export function UserNotificationProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [openChatCount, setOpenChatCount] = useState(0);
  const [hasNewChatNotification, setHasNewChatNotification] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);

  const stompClientRef = useRef(null);
  const subscriptionRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const { user } = useAuth();

  // Heartbeat interval: 60 seconds (60000ms) - configurable
  const HEARTBEAT_INTERVAL = 60000;
  const RECONNECT_DELAY = 5000;

  // Create a ref to store the connect function to avoid circular dependency
  const connectRef = useRef(null);

  /**
   * Handle incoming notification
   */
  const handleNotification = useCallback((notification) => {

    setNotifications(prev => [...prev, notification]);

    // Check if it's a NEW_CHAT notification (case-insensitive comparison)
    if (notification.type === NOTIFICATION_TYPES.NEW_CHAT || notification.type === 'NEW_CHAT') {
      // Update open chat count
      setOpenChatCount(prev => {
        const newCount = prev + 1;
        return newCount;
      });
      setHasNewChatNotification(true);
    } else {
      switch (notification.type) {
        case NOTIFICATION_TYPES.SYSTEM_ALERT:
        case NOTIFICATION_TYPES.SERVICE_STATUS:
        case NOTIFICATION_TYPES.BROADCAST:
          // Handle other notification types as needed
          break;

        default:
      }
    }
  }, []);

  /**
   * Register user with backend
   */
  const registerUser = useCallback((stompClient) => {
    const tenantId = getTenantId(user);
    if (!tenantId || !user) {
      return;
    }

    const registrationMessage = {
      type: NOTIFICATION_TYPES.REGISTER,
      tenantId: parseInt(tenantId),
      userId: user.id || user.user_id,
      userName: user.first_name || user.firstName || user.email || 'User',
      data: {
        userId: user.id || user.user_id,
        userName: user.first_name || user.firstName || user.email || 'User'
      }
    };

    try {
      stompClient.send('/app/user.presence', {}, JSON.stringify(registrationMessage));
    } catch (err) {
      console.error('UserNotification: Failed to register user:', err);
    }
  }, [user]);

  /**
   * Subscribe to notification topics
   */
  const subscribeToNotifications = useCallback((stompClient) => {
    const tenantId = getTenantId(user);
    if (!tenantId) {
      return;
    }

    const tenantIdInt = parseInt(tenantId);

    // Subscribe to tenant-specific notifications
    const tenantTopic = `/topic/user-notifications/${tenantIdInt}`;
    subscriptionRef.current = stompClient.subscribe(tenantTopic, (message) => {
      try {
        const notification = JSON.parse(message.body);
        // Call handleNotification directly with proper state setters
        handleNotification(notification);
      } catch (err) {
        console.error('UserNotification: Failed to parse notification:', err);
      }
    });

    // Optionally subscribe to global notifications
    const globalTopic = '/topic/user-notifications/all';
    stompClient.subscribe(globalTopic, (message) => {
      try {
        const notification = JSON.parse(message.body);
        handleNotification(notification);
      } catch (err) {
        console.error('UserNotification: Failed to parse global notification:', err);
      }
    });

  }, [user, handleNotification]);

  /**
   * Schedule reconnection attempt
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      // Check auth before reconnecting
      const tenantId = getTenantId(user);
      const authToken = getAuthToken();
      if (user && tenantId && authToken && (!stompClientRef.current || !stompClientRef.current.connected)) {
        if (connectRef.current) {
          connectRef.current();
        }
      }
    }, RECONNECT_DELAY);
  }, [user]);

  /**
   * Start heartbeat monitoring
   */
  const startHeartbeatMonitoring = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Monitor connection health
    heartbeatIntervalRef.current = setInterval(() => {
      if (stompClientRef.current && !stompClientRef.current.connected) {
        setIsConnected(false);
        scheduleReconnect();
      }
    }, HEARTBEAT_INTERVAL);
  }, [scheduleReconnect]);

  /**
   * Initialize STOMP connection
   */
  const connect = useCallback(() => {
    if (stompClientRef.current && stompClientRef.current.connected) {
      return;
    }

    const tenantId = getTenantId(user);
    const authToken = getAuthToken();
    if (!user || !tenantId || !authToken) {
      return;
    }

    const wsUrl = WS_URL + '/ws/user-notifications';

    try {
      // Use factory function for auto-reconnect support
      const socketFactory = () => {
        return new WebSocket(wsUrl);
      };

      const stompClient = Stomp.over(socketFactory, {
        debug: (str) => {
          // Log all important messages for debugging
          if (str.includes('ERROR') || str.includes('Connected') || str.includes('Disconnected') || str.includes('Opening')) {
            console.log('UserNotification STOMP:', str);
          }
        },
        reconnectDelay: RECONNECT_DELAY,
        heartbeatIncoming: HEARTBEAT_INTERVAL,
        heartbeatOutgoing: HEARTBEAT_INTERVAL
      });

      stompClient.connect({}, (frame) => {
        setIsConnected(true);
        setError(null);
        stompClientRef.current = stompClient;

        // Register user with backend
        registerUser(stompClient);

        // Subscribe to notifications
        subscribeToNotifications(stompClient);

        // Start heartbeat monitoring
        startHeartbeatMonitoring();
      }, (error) => {
        console.error('UserNotification: STOMP connection error:', error);
        setError('Failed to connect to notification server');
        setIsConnected(false);

        // Attempt reconnection after delay
        scheduleReconnect();
      });

    } catch (err) {
      console.error('UserNotification: Connection error:', err);
      setError('Failed to initialize connection');
      setIsConnected(false);
      scheduleReconnect();
    }
  }, [user, registerUser, subscribeToNotifications, startHeartbeatMonitoring, scheduleReconnect]);

  // Store connect function in ref to avoid circular dependency
  connectRef.current = connect;


  /**
   * Disconnect from STOMP
   */
  const disconnect = useCallback(() => {

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (stompClientRef.current) {
      if (stompClientRef.current.connected) {
        try {
          // Disconnect with a callback to ensure it completes
          stompClientRef.current.disconnect(() => {
            // console.log('UserNotification: STOMP disconnect callback executed');
          });
        } catch (error) {
          // console.error('UserNotification: Error during STOMP disconnect:', error);
        }
      } else {
        // console.log('UserNotification: STOMP client was not connected');
      }
      stompClientRef.current = null;
    }

    setIsConnected(false);
  }, []);

  /**
   * Clear new chat notification
   */
  const clearNewChatNotification = useCallback(() => {
    setHasNewChatNotification(false);
  }, []);

  /**
   * Refresh open chat count
   */
  const refreshOpenChatCount = useCallback((count) => {
    setOpenChatCount(count);
  }, []);

  // Connect on mount and when user changes
  useEffect(() => {
    const tenantId = getTenantId(user);
    const authToken = getAuthToken();
    if (tenantId && user && authToken) {
      connect();
    } else {
      // User is null or missing auth context - disconnect and reset state
      disconnect();
      setOpenChatCount(0);
      setHasNewChatNotification(false);
      setNotifications([]);
    }

    return () => {
      // Cleanup on unmount or when user changes
      disconnect();
    };
  }, [user, connect, disconnect]);

  const value = {
    isConnected,
    openChatCount,
    hasNewChatNotification,
    notifications,
    error,
    connect,
    disconnect,
    clearNewChatNotification,
    refreshOpenChatCount
  };

  return (
    <UserNotificationContext.Provider value={value}>
      {children}
    </UserNotificationContext.Provider>
  );
}

export function useUserNotifications() {
  const context = useContext(UserNotificationContext);
  if (!context) {
    throw new Error('useUserNotifications must be used within UserNotificationProvider');
  }
  return context;
}

